import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IonItemSliding, IonItem, IonItemOptions, IonItemOption } from '@ionic/react';
import { Icon } from '../../icons';
import { Badge, Btn, Card, FormRow, Field, Tabs, Modal, FormActions } from '../../components/ui';
import { ConfirmModal } from '../../components/shared';
import ElectionService from '../../api/election';
import { toast } from 'react-hot-toast';
import { saveAndOpenFile } from '../../utils/fileUtils';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';

const translations = { en, hi, gu };

const CandidateRing = ({ pct, color, size = 56, mounted }) => {
  const r = (size - 8) / 2, circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={6} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6} strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={mounted ? circ - (circ * pct / 100) : circ}
        style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%", transition: "stroke-dashoffset 1.2s cubic-bezier(.34,1.56,.64,1)" }} />
    </svg>
  );
};

const ResultsModal = ({ election, onClose, t, mounted }) => {
  const [downloading, setDownloading] = useState(false);
  const sorted = [...(election.candidates || [])].sort((a, b) => (b.votes_count || 0) - (a.votes_count || 0));
  const winner = sorted[0];
  const votesCastCount = Number(election.votescast ?? election.votes_cast ?? 0);
  const total = votesCastCount || 1;
  const CCOLORS = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
  const totalVoters = Number(election.total_voters) || 0;
  const turnout = Math.round((votesCastCount / Math.max(1, totalVoters)) * 100);

  const handleDownloadReport = async () => {
    setDownloading(true);
    const toastId = toast.loading(t("msg_generating_report") || "Generating election report...");
    try {
      const candidates = [...(election.candidates || [])].sort((a, b) => (b.votes_count || 0) - (a.votes_count || 0));
      const totalVotes = Number(election.votescast ?? election.votes_cast ?? 0);
      const votersCount = Number(election.total_voters ?? 0);
      const turnoutPct = Math.round((totalVotes / Math.max(1, votersCount)) * 100);

      let csv = `Election Report: ${election.title || 'Untitled Election'}\n`;
      csv += `Position: ${election.position || 'N/A'}\n`;
      csv += `Status: ${election.status || 'N/A'}\n`;
      csv += `Total Voters: ${votersCount}\n`;
      csv += `Votes Cast: ${totalVotes}\n`;
      csv += `Turnout: ${turnoutPct}%\n\n`;
      csv += `Candidate,Flat,Nominated By,Votes,Percentage\n`;

      candidates.forEach(c => {
        const pct = totalVotes > 0 ? Math.round(((c.votes_count || 0) / totalVotes) * 100) : 0;
        csv += `"${(c.name || '').replace(/"/g, '""')}","${(c.flat || '').replace(/"/g, '""')}","${(c.nominated || '').replace(/"/g, '""')}",${c.votes_count || 0},${pct}%\n`;
      });

      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const safeTitle = (election.title || 'Report').replace(/[/\\?%*:|"<>]/g, '_').replace(/\s+/g, '_');
      const filename = `Election_Report_${safeTitle}.csv`;

      await saveAndOpenFile(blob, filename);
      toast.success(t("toast_election_report_downloaded") || "Election report downloaded successfully", { id: toastId });
    } catch (err) {
      console.error("Failed to download election report", err);
      if (err?.message && (err.message.includes("canceled") || err.message.includes("cancelled") || err.message.includes("Share canceled"))) {
        toast.dismiss(toastId);
      } else {
        toast.error(err?.message || t("toast_election_report_failed") || "Failed to download election report", { id: toastId });
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal 
      title={`${t("title_results")} - ${election.title}`} 
      onClose={onClose} 
      wide
      footer={
        <Btn 
          icon="download" 
          full 
          onClick={handleDownloadReport} 
          loading={downloading}
          variant="primary"
          className="w-full"
        >
          {t("btn_download_report")}
        </Btn>
      }
    >
      {winner ? (
        <div className="rounded-xl overflow-hidden mb-5" style={{ background: "linear-gradient(135deg,#0f172a,#1e1b4b)" }}>
          <div className="p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-full gradient-primary flex items-center justify-center text-white font-black text-lg flex-shrink-0" style={{ boxShadow: "0 0 0 3px rgba(79,70,229,0.4)" }}>{winner.avatar}</div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <Icon name="trophy" size={14} color="#fbbf24" />
                <span className="text-amber-400 text-xs font-bold uppercase tracking-wider">{election.status === "Completed" ? t("badge_elected") : t("badge_leading")}</span>
              </div>
              <div className="text-white font-black text-lg">{winner.name}</div>
              <div className="text-white/50 text-xs">{winner.flat} · {t("text_nominated_by")} {winner.nominated}</div>
            </div>
            <div className="text-right">
              <div className="text-white/40 text-xs mb-0.5">{t("text_vote_share")}</div>
              <div className="text-white font-black text-2xl">{Math.round(((winner.votes_count || 0) / total) * 100)}%</div>
              <div className="text-white/40 text-xs">{winner.votes_count || 0} {t("text_of")} {total} {t("text_votes")}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-slate-100 dark:bg-slate-800 rounded-xl p-5 mb-5 text-center text-muted">
          {t("msg_no_candidates") || "No candidates available for this election."}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {[[t("stat_total_voters"), election.total_voters, "blue"], [t("stat_votes_cast"), election.votescast, "green"], [t("stat_turnout"), `${turnout}%`, "purple"]].map(([l, v, c]) => (
          <div key={l} className={`rounded-xl p-3.5 text-center ${c === "blue" ? "bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-900" : c === "green" ? "bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-100 dark:border-emerald-900" : "bg-purple-50 dark:bg-purple-900/50 border border-purple-100 dark:border-purple-900"}`}>
            <div className={`text-xl font-black ${c === "blue" ? "text-blue-700 dark:text-blue-600" : c === "green" ? "text-emerald-700 dark:text-emerald-600" : "text-purple-700 dark:text-purple-600"}`}>{v}</div>
            <div className="text-xs text-muted dark:text-slate-400 mt-0.5">{l}</div>
          </div>
        ))}
      </div>

      <div className="space-y-3 mb-4">
        {sorted.map((c, i) => {
          const pct = Math.round(((c.votes_count || 0) / total) * 100);
          const color = CCOLORS[i] || "#94a3b8";
          return (
            <div key={c.id} className={`p-4 rounded-xl border ${i === 0 ? "border-primary/20 bg-primary-light/50 dark:border-slate-700 dark:bg-slate-800/80" : "border-border dark:border-slate-700 dark:bg-slate-800/60"}`}>
              <div className="flex items-center gap-3 mb-2">
                <CandidateRing pct={pct} color={color} size={44} mounted={mounted} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-ink text-sm">{c.name}</span>
                    {i === 0 && <span className="text-sm font-bold text-primary dark:text-indigo-300 bg-primary/10 dark:bg-indigo-800/20 px-2 py-0.5 rounded-full">{election.status === "Completed" ? t("badge_winner") : t("badge_leading")}</span>}
                  </div>
                  <div className="text-xs text-muted">{c.flat}</div>
                </div>
                <div className="text-right">
                  <div className="font-black text-ink">{c.votes_count || 0} <span className="text-xs font-normal text-muted">{t("text_votes_lc")}</span></div>
                  <div className="text-xs font-bold" style={{ color }}>{pct}%</div>
                </div>
              </div>
              <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-1000" style={{ width: mounted ? `${pct}%` : "0%", background: color }} />
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

const VotingBoothModal = ({ election, onClose, t, castVote, voted }) => {
  const [selected, setSelected] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const alreadyVoted = voted[election.id];
  return (
    <Modal title={`${t("title_cast_vote")} - ${election.position}`} onClose={onClose}>
      {alreadyVoted ? (
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4"><Icon name="check" size={28} color="#10b981" strokeWidth={2.5} /></div>
          <div className="text-lg font-bold text-ink mb-1">{t("title_vote_recorded")}</div>
          <div className="text-sm text-muted mb-4">{t("msg_vote_recorded")}</div>
          <div className="bg-slate-50 dark:bg-slate-800 border border-border dark:border-slate-700 rounded-xl p-4 mb-4">
            <div className="text-xs text-muted mb-1">{t("text_voted_for")}</div>
            <div className="font-bold text-ink">{election.candidates?.find(c => c.id === alreadyVoted)?.name || t("text_unknown")}</div>
          </div>
          <Btn full onClick={onClose}>{t("btn_close")}</Btn>
        </div>
      ) : confirmed ? (
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4"><Icon name="check" size={28} color="#10b981" strokeWidth={2.5} /></div>
          <div className="text-lg font-bold text-ink mb-2">{t("title_vote_submitted")}</div>
          <div className="text-sm text-muted mb-5">{t("msg_vote_submitted")}</div>
          <Btn full variant="primary" onClick={onClose}>{t("btn_close")}</Btn>
        </div>
      ) : (
        <>
          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2.5 text-xs text-amber-800 dark:text-amber-200 flex items-center gap-2 mb-4">
            <Icon name="shield" size={13} color="#d97706" className="flex-shrink-0" />
            {t("msg_vote_anonymous")}
          </div>
          <div className="space-y-2.5 mb-5">
            {election.candidates?.map((c) => (
              <div key={c.id} onClick={() => setSelected(c.id)}
                className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${selected === c.id ? "border-indigo-500 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-950/30" : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"}`}>
                <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{c.avatar}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-bold text-ink">{c.name}</span>
                    <span className="text-xs text-muted">Flat {c.flat}</span>
                  </div>
                  <div className="text-xs text-muted mb-1.5">{t("text_resident_since")} {c.since} · {t("text_nominated_by")} {c.nominated}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed line-clamp-2">{c.bio}</div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 transition-all ${selected === c.id ? "border-primary bg-primary" : "border-slate-300 dark:border-slate-600"}`}>
                  {selected === c.id && <div className="w-2 h-2 rounded-full bg-white" />}
                </div>
              </div>
            ))}
          </div>
          <Btn full variant="primary" icon="vote" disabled={!selected} onClick={() => { castVote(election.id, selected); setConfirmed(true); }}>{t("btn_submit_vote")}</Btn>
        </>
      )}
    </Modal>
  );
};

export default function ElectionScreen() {
  const { language = 'en' } = useSettings();
  const t = (key, params) => {
    let str = translations[language]?.hr?.[key] || translations.en?.hr?.[key] || key;
    if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
    return str;
  };
  const { user } = useAuth();
  const isAdmin = user?.roles_list?.some(r => ['super_admin', 'admin'].includes(r));
  const isMember = (user?.primary_role_label === 'Member' || user?.roles_list?.includes('member')) && !isAdmin;
  const [tab, setTab] = useState(0);
  const [elModal, setElModal] = useState(false);
  const [voteModal, setVoteModal] = useState(null);
  const [resultModal, setResultModal] = useState(null);
  const [voted, setVoted] = useState({});
  const [delEl, setDelEl] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [elections, setElections] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setTimeout(() => setMounted(true), 300); }, []);

  const fetched = useRef(false);

  const fetchElections = async () => {
    setLoading(true);
    try {
      const response = await ElectionService.getElections();
      // Axios interceptor returns response.data directly, so response is the array
      const electionsData = Array.isArray(response) ? response : (response?.data || []);
      setElections(electionsData);

      // Sync voted state based on backend response
      const serverVoted = {};
      electionsData.forEach(el => {
        if (el.has_voted) serverVoted[el.id] = el.user_voted_candidate_id;
      });
      setVoted(serverVoted);
    } catch (err) {
      toast.error(t("toast_load_elections_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    fetchElections();
  }, []);

  const defEl = { title: "", position: "Chairman", description: "", startDate: "", endDate: "", eligibility: "All Members", nominees: "" };
  const [elForm, setElForm] = useState(defEl);
  const [errors, setErrors] = useState({});
  const ef = k => e => {
    setElForm(f => ({ ...f, [k]: e.target.value }));
    setErrors(err => {
      if (!err[k] && !(k === 'startDate' && err.endDate)) return err;
      const next = { ...err, [k]: null };
      if (k === 'startDate' && next.endDate === t("val_election_end_date_invalid")) {
        next.endDate = null;
      }
      return next;
    });
  };

  const statusColor = { Active: "green", Upcoming: "blue", Completed: "gray" };


  const castVote = async (electionId, candidateId) => {
    try {
      await ElectionService.castVote(electionId, candidateId);
      toast.success(t("toast_vote_cast"));
      setVoted(v => ({ ...v, [electionId]: candidateId }));
      setElections(els => els.map(e => e.id === electionId ? {
        ...e,
        votescast: e.votescast + 1,
        candidates: e.candidates.map(c => c.id === candidateId ? { ...c, votes_count: (c.votes_count || 0) + 1 } : c)
      } : e));
    } catch (err) {
      toast.error(err.response?.data?.message || t("toast_vote_failed"));
    } finally {
      setVoteModal(null);
    }
  };

  const addElection = async () => {
    const newErrors = {};
    if (!elForm.title?.trim()) newErrors.title = t("val_election_title");
    if (!elForm.position) newErrors.position = t("val_election_position");
    if (!elForm.startDate) newErrors.startDate = t("val_election_start_date");
    if (!elForm.endDate) {
      newErrors.endDate = t("val_election_end_date");
    } else if (elForm.startDate && new Date(elForm.endDate) < new Date(elForm.startDate)) {
      newErrors.endDate = t("val_election_end_date_invalid");
    }
    if (!elForm.nominees?.trim()) newErrors.nominees = t("val_election_nominees");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error(t("toast_fill_required"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: elForm.title,
        position: elForm.position,
        description: elForm.description,
        start_date: elForm.startDate,
        end_date: elForm.endDate,
        eligibility: elForm.eligibility,
        nominees: elForm.nominees
      };

      const createdData = await ElectionService.createElection(payload);
      createdData.votescast = 0; // Initialize for UI
      setElections(els => [createdData, ...els]);
      setElModal(false);
      setElForm(defEl);
      setErrors({});
      toast.success(t("toast_election_created"));
    } catch (err) {
      toast.error(err.response?.data?.message || t("toast_election_failed"));
    } finally {
      setSaving(false);
    }
  };

  const updateElectionStatus = async (id, status) => {
    try {
      await ElectionService.updateElection(id, { status });
      setElections(els => els.map(e => e.id === id ? { ...e, status } : e));
      toast.success(t("toast_election_status", { status: t("status_" + status.toLowerCase()) || status }));
    } catch (err) {
      toast.error(t("toast_update_status_failed"));
    }
  };

  const deleteElection = async () => {
    if (!delEl) return;
    try {
      await ElectionService.deleteElection(delEl.id);
      setElections(els => els.filter(e => e.id !== delEl.id));
      toast.success(t("toast_election_deleted"));
    } catch (err) {
      toast.error(t("toast_delete_election_failed"));
    } finally {
      setDelEl(null);
    }
  };



  const safeElections = Array.isArray(elections) ? elections : [];
  const active = safeElections.filter(e => e.status === "Active");
  const upcoming = safeElections.filter(e => e.status === "Upcoming");
  const completed = safeElections.filter(e => e.status === "Completed");

  return (
    <div className="fade-up space-y-4">
      {loading && elections.length === 0 && <div className="py-12 text-center text-muted">{t("msg_loading")}</div>}

      {voteModal && <VotingBoothModal election={voteModal} onClose={() => setVoteModal(null)} t={t} castVote={castVote} voted={voted} />}
      {resultModal && <ResultsModal election={resultModal} onClose={() => setResultModal(null)} t={t} mounted={mounted} />}
      {delEl && <ConfirmModal title={t("title_delete_election")} message={t("msg_delete_election", { title: delEl.title })} onConfirm={deleteElection} onCancel={() => setDelEl(null)} />}

      {elModal && (
        <Modal 
          title={t("title_new_election")} 
          onClose={() => { setElModal(false); setElForm(defEl); setErrors({}); }} 
          wide
          footer={
            <FormActions 
              onSave={addElection} 
              onCancel={() => { setElModal(false); setElForm(defEl); setErrors({}); }} 
              label={t("btn_create_election")} 
              loading={saving} 
              plain
            />
          }
        >
          <FormRow cols={2}><Field label={t("label_title")} value={elForm.title} onChange={ef("title")} required error={errors.title} placeholder={t("placeholder_title")} /><Field label={t("label_position")} type="select" value={elForm.position} onChange={ef("position")} required error={errors.position} options={["Chairman", "Vice Chairman", "Secretary", "Joint Secretary", "Treasurer", "Committee Member"].map(p => ({ value: p, label: t("pos_" + p.toLowerCase().replace(/ /g, "_")) || p }))} /></FormRow>
          <FormRow><Field label={t("label_description")} type="textarea" value={elForm.description} onChange={ef("description")} rows={2} placeholder={t("placeholder_description")} /></FormRow>
          <FormRow cols={2}><Field label={t("label_start_date")} type="date" value={elForm.startDate} onChange={ef("startDate")} required error={errors.startDate} /><Field label={t("label_end_date")} type="date" value={elForm.endDate} onChange={ef("endDate")} required error={errors.endDate} /></FormRow>
          <FormRow cols={2}><Field label={t("label_eligibility")} type="select" value={elForm.eligibility} onChange={ef("eligibility")} options={["All Members", "Flat Owners Only", "Registered Voters Only", "Committee Members"].map(e => ({ value: e, label: t("elig_" + e.toLowerCase().replace(/ /g, "_")) || e }))} /><Field label={t("label_nominees")} value={elForm.nominees} onChange={ef("nominees")} required error={errors.nominees} placeholder={t("placeholder_nominees")} /></FormRow>
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-lg px-4 py-2.5 text-xs text-blue-800 dark:text-blue-200 flex items-center gap-2 mt-1 mb-2">
            <Icon name="info" size={13} color="#3b82f6" className="flex-shrink-0" />
            <span dangerouslySetInnerHTML={{ __html: t("msg_election_upcoming") }} />
          </div>
        </Modal>
      )}

      {active.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-bold text-ink">{t("title_live_elections")}</span>
            </div>
            {!isMember && <div className="hidden lg:block"><Btn sm variant="primary" icon="plus" onClick={() => setElModal(true)}>{t("btn_new_election")}</Btn></div>}
          </div>
          <div className="space-y-4">
            {active.map(el => {
              const turnout = Math.round((el.votescast / Math.max(1, el.total_voters)) * 100);
              const sorted = [...(el.candidates || [])].sort((a, b) => (b.votes_count || 0) - (a.votes_count || 0));
              const hasVoted = !!voted[el.id];
              const CCOLORS = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444"];
              return (
                <div key={el.id} className="border-2 border-primary/20 dark:border-slate-700/60 rounded-xl2 overflow-hidden bg-white dark:bg-slate-900">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between p-5 border-b border-primary/10 dark:border-slate-700/60 gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <Badge color="green" dot>{t("badge_voting_open")}</Badge>
                        <span className="text-sm text-muted">· {t("text_closes")} {el.end_date}</span>
                      </div>
                      <div className="text-base font-black text-ink">{el.title}</div>
                      <div className="text-xs text-muted mt-0.5">{t("label_position")}: <span className="text-primary font-semibold">{t("pos_" + el.position.toLowerCase().replace(/ /g, "_")) || el.position}</span> · {t("text_eligible")} {t("elig_" + el.eligibility.toLowerCase().replace(/ /g, "_")) || el.eligibility}</div>
                    </div>
                    <div className="flex flex-wrap lg:flex-nowrap gap-2 lg:flex-shrink-0 mt-2 lg:mt-0">
                      {hasVoted
                        ? <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-300 font-bold bg-emerald-50 dark:bg-emerald-900/30 px-3 py-1.5 rounded-lg border border-emerald-100 dark:border-emerald-800"><Icon name="check" size={11} color="#10b981" /> {t("badge_voted")}</div>
                        : <Btn sm variant="primary" icon="vote" onClick={() => setVoteModal(el)}>{t("btn_cast_vote")}</Btn>
                      }
                      <Btn sm onClick={() => setResultModal(el)}>{t("btn_live_results")}</Btn>
                      {!isMember && <Btn xs variant="danger" icon="trash" onClick={() => setDelEl(el)} />}
                    </div>
                  </div>
                  <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    <div className="flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-800 rounded-xl border border-border dark:border-slate-700 p-4">
                      <div className="relative">
                        <svg width={88} height={88} viewBox="0 0 88 88">
                          <circle cx={44} cy={44} r={36} fill="none" stroke="#e2e8f0" className="dark:stroke-slate-700" strokeWidth={8} />
                          <circle cx={44} cy={44} r={36} fill="none" stroke="#4f46e5" strokeWidth={8} strokeLinecap="round"
                            strokeDasharray={2 * Math.PI * 36} strokeDashoffset={mounted ? (2 * Math.PI * 36) * (1 - turnout / 100) : 2 * Math.PI * 36}
                            style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%", transition: "stroke-dashoffset 1.4s cubic-bezier(.34,1.56,.64,1)" }} />
                          <text x={44} y={40} textAnchor="middle" fontSize={18} fontWeight={900} fill="#0f172a" className="dark:fill-slate-100">{turnout}%</text>
                          <text x={44} y={54} textAnchor="middle" fontSize={8} fill="#94a3b8" className="dark:fill-slate-400" fontWeight="600">{t("text_turnout").toUpperCase()}</text>
                        </svg>
                      </div>
                      <div className="text-center mt-2">
                        <div className="text-xs font-bold text-ink dark:text-slate-100">{el.votescast} / {el.total_voters}</div>
                        <div className="text-sm text-muted dark:text-slate-400">{t("text_votes_cast_lc")}</div>
                      </div>
                    </div>
                    <div className="col-span-1 sm:col-span-1 lg:col-span-2 space-y-2.5">
                      <div className="text-xs font-bold text-muted dark:text-slate-400 uppercase tracking-wider mb-2">{t("text_candidates")}</div>
                      {sorted.map((c, i) => {
                        const pct = el.votescast > 0 ? Math.round(((c.votes_count || 0) / el.votescast) * 100) : 0;
                        return (
                          <div key={c.id} className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{c.avatar}</div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="font-semibold text-ink dark:text-slate-100">{c.name} {c.flat && <span className="text-muted dark:text-slate-400 font-normal">· {c.flat}</span>}</span>
                                <span className="font-bold" style={{ color: CCOLORS[i] }}>{pct}% · {c.votes_count || 0} {t("text_votes_lc")}</span>
                              </div>
                              <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-1000" style={{ width: mounted ? `${pct}%` : "0%", background: CCOLORS[i] }} />
                              </div>
                            </div>
                            {i === 0 && el.votescast > 0 && <Icon name="crown" size={14} color="#f59e0b" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Tabs tabs={[t("tab_all_elections"), t("tab_upcoming"), t("tab_completed")]} active={tab === 0 ? 0 : tab === 1 ? 1 : 2} onChange={i => setTab(i)} />
        {active.length === 0 && !isMember && <div className="hidden lg:block"><Btn sm variant="primary" icon="plus" onClick={() => setElModal(true)}>{t("btn_new_election")}</Btn></div>}
      </div>

      {(() => {
        const list = tab === 0 ? safeElections : tab === 1 ? upcoming : completed;

        if (list.length === 0) {
          return (
            <Card>
              <div className="py-10 text-center text-muted">
                <div className="mb-2"><Icon name="election" size={24} className="opacity-20 mx-auto" /></div>
                <div className="text-sm font-medium">{tab === 1 ? t("msg_no_upcoming") : tab === 2 ? t("msg_no_completed") : t("msg_no_elections")}</div>
              </div>
            </Card>
          );
        }

        return (
          <>
            <div className="hidden lg:block space-y-4">
              {list.map(el => {
                const turnout = el.votescast > 0 ? Math.round((el.votescast / Math.max(1, el.total_voters)) * 100) : 0;
                const sorted = [...(el.candidates || [])].sort((a, b) => (b.votes_count || 0) - (a.votes_count || 0));
                return (
                  <Card key={el.id}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${el.status === "Completed" ? "bg-slate-100 dark:bg-slate-800" : el.status === "Active" ? "bg-emerald-50 dark:bg-emerald-900/30" : "bg-blue-50 dark:bg-blue-900/30"}`}>
                          <Icon name={el.status === "Completed" ? "trophy" : el.status === "Active" ? "vote" : "election"} size={22} className={el.status === "Completed" ? "text-amber-500" : el.status === "Active" ? "text-emerald-500" : "text-blue-500"} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className="font-bold text-ink">{el.title}</span>
                            <Badge color={statusColor[el.status]}>{t("status_" + el.status.toLowerCase()) || el.status}</Badge>
                          </div>
                          <div className="text-xs text-muted mb-2">{el.start_date} → {el.end_date} · {el.eligibility}</div>
                          <div className="flex gap-1.5 flex-wrap">
                            {sorted.map((c, i) => (
                              <div key={c.id} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${el.status === "Completed" && i === 0 ? "bg-amber-50 dark:bg-amber-600 text-amber-800 dark:text-white border border-amber-100 dark:border-amber-600" : "bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600"}`}>
                                {el.status === "Completed" && i === 0 && <Icon name="trophy" size={10} className="text-[#d97706] dark:text-white" />}
                                <span>{c.name}</span>
                                {el.status !== "Upcoming" && <span className="text-muted dark:text-white">{el.votescast > 0 ? Math.round((c.votes_count || 0) / el.votescast * 100) : 0}%</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        {el.status !== "Upcoming" && (
                          <div className="text-right">
                            <div className="text-xs text-muted">{t("text_turnout")}</div>
                            <div className="text-lg font-black text-ink">{turnout}%</div>
                            <div className="text-sm text-muted">{el.votescast} / {el.total_voters} {t("text_votes_lc")}</div>
                          </div>
                        )}
                        {el.status === "Upcoming" && (
                          <div className="text-right">
                            <div className="text-xs text-muted">{t("text_opens")}</div>
                            <div className="text-sm font-bold text-blue-600">{el.start_date}</div>
                            <div className="text-sm text-muted">{(el.candidates || []).length} {t("text_candidates_lc")}</div>
                          </div>
                        )}
                        <div className="flex gap-1.5">
                          {el.status === "Completed" && <Btn xs icon="trophy" onClick={() => setResultModal(el)}>{t("btn_results")}</Btn>}
                          {el.status === "Upcoming" && !isMember && <Btn xs variant="primary" onClick={() => updateElectionStatus(el.id, "Active")}>{t("btn_activate")}</Btn>}
                          {el.status === "Active" && !isMember && <Btn xs onClick={() => updateElectionStatus(el.id, "Completed")}>{t("btn_end_election")}</Btn>}
                          {!isMember && <Btn xs variant="danger" icon="trash" onClick={() => setDelEl(el)} />}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Mobile View */}
            <div className="block lg:hidden space-y-3 mt-3">
              {list.map((el, i) => {
                const turnout = el.votescast > 0 ? Math.round((el.votescast / Math.max(1, el.total_voters)) * 100) : 0;
                const sorted = [...(el.candidates || [])].sort((a, b) => (b.votes_count || 0) - (a.votes_count || 0));
                
                return (
                  <IonItemSliding key={el.id} className={`rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden ${i === 0 ? 'animate-swipe-hint' : ''}`} style={{ '--background': 'transparent' }}>
                    <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0', '--inner-padding-end': '0' }}>
                      <div className="flex w-full items-start gap-3 p-3 bg-white dark:bg-slate-900">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 ${el.status === "Completed" ? "bg-slate-100 dark:bg-slate-800" : el.status === "Active" ? "bg-emerald-50 dark:bg-emerald-900/30" : "bg-blue-50 dark:bg-blue-900/30"}`}>
                          <Icon name={el.status === "Completed" ? "trophy" : el.status === "Active" ? "vote" : "election"} size={16} className={el.status === "Completed" ? "text-amber-500" : el.status === "Active" ? "text-emerald-500" : "text-blue-500"} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-bold text-ink truncate mb-1">{el.title}</div>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge color={statusColor[el.status]} sm>{t("status_" + el.status.toLowerCase()) || el.status}</Badge>
                            <span className="text-[10px] text-muted">{el.eligibility}</span>
                          </div>
                          {el.status !== "Upcoming" && (
                            <div className="text-[10px] text-muted mt-1.5 font-medium">
                              Turnout: <span className="font-bold text-ink">{turnout}%</span> ({el.votescast}/{el.total_voters})
                            </div>
                          )}
                          {el.status === "Upcoming" && (
                            <div className="text-[10px] text-muted mt-1.5 font-medium">
                              Opens: <span className="font-bold text-blue-600">{el.start_date}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col items-end justify-start gap-1 flex-shrink-0 text-right mt-1 ml-1">
                          <div className="text-[10px] font-bold text-primary">{t("pos_" + el.position.toLowerCase().replace(/ /g, "_")) || el.position}</div>
                          <div className="text-[9px] text-muted whitespace-nowrap">{el.start_date.substring(5)} to {el.end_date.substring(5)}</div>
                          {sorted.length > 0 && (
                            <div className="text-[9px] font-medium text-slate-500 mt-0.5">
                              {sorted.length} {t("text_candidates_lc")}
                            </div>
                          )}
                        </div>

                        <div className="text-slate-300 flex flex-col items-center justify-center pt-3 gap-2 ml-1">
                          {el.status === "Completed" && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); setResultModal(el); }} className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-50 text-amber-500">
                              <Icon name="trophy" size={14} />
                            </button>
                          )}
                          <Icon name="chevron_left" size={14} />
                        </div>
                      </div>
                    </IonItem>
                    
                    <IonItemOptions side="end">
                      {el.status === "Upcoming" && !isMember && (
                        <IonItemOption color="primary" onClick={() => updateElectionStatus(el.id, "Active")}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="check" size={20} color="white" />
                          </div>
                        </IonItemOption>
                      )}
                      {el.status === "Active" && !isMember && (
                        <IonItemOption color="warning" onClick={() => updateElectionStatus(el.id, "Completed")}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="x" size={20} color="white" />
                          </div>
                        </IonItemOption>
                      )}
                      {!isMember && (
                        <IonItemOption color="danger" onClick={() => setDelEl(el)}>
                          <div className="flex flex-col items-center justify-center w-12">
                            <Icon name="trash" size={20} color="white" />
                          </div>
                        </IonItemOption>
                      )}
                    </IonItemOptions>
                  </IonItemSliding>
                );
              })}
            </div>
          </>
        );
      })()}

      {/* FAB for Mobile */}
      {!isMember && !elModal && !voteModal && !resultModal && !delEl && createPortal(
        <div className="fixed bottom-6 right-6 z-50 lg:hidden pointer-events-auto">
          <button
            onClick={() => setElModal(true)}
            className="w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:scale-105 transition-transform active:scale-95 border-0 cursor-pointer"
          >
            <Icon name="plus" size={24} />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
